import React, { useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import {
  NewAssistant,
  AssistantDetail,
  ConfigureWebhook,
  AssistantList,
} from "../platforms/Assistant";
import { AssistantDetails, ComingSoon } from "../platforms/AssistantDetails";
import Settings from "../platforms/Setting/Settings";
import Assistants from "../platforms/Assistants";
import ConfigurationForm from "../platforms/Configuration/ConfigurationForm";
import Platform from "../platforms/Platform/Platform";
import NewPlatform from "../platforms/Platform/NewPlatform";
import SettingsForm from "../platforms/Setting/SettingsForm";
import ToolEditor from "../platforms/Tools/ToolEditor";
import Profile from "../platforms/Profile";
import Users from "../platforms/Users/Users";
import AddUser from "../platforms/Users/NewUser";
import PlatformWorkspace from "../platforms/OAuth/PlatformWorkspace";
import AgentTabs from "../platforms/Agent/AgentTabs";
import MeetingsList from "../platforms/Meetings/MeetingsList";
import CreateMeeting from "../platforms/Meetings/CreateMeeting";
import MeetingDetails from "../platforms/Meetings/MeetingDetail";
import CreateAgent from "../platforms/Agent/CreateAgent";
import Participants from "../platforms/Participants/Participants";
import CreateParticipant from "../platforms/Participants/CreateParticipant";
import Billing from "../platforms/Billing/Billing";
import Pricing from "../platforms/Billing/Pricing";
import CheckoutForm from "../platforms/Billing/CheckoutForm";
import PlanDetails from "../platforms/Billing/PlanDetails";
import Integrations from "../platforms/Integrations/Integrations";
import Apps from "../platforms/Onboarding/Apps";
import Meetings from "../platforms/Onboarding/Meetings";
import Plans from "../platforms/Onboarding/Plans";
import NotFound from "../components/common/NotFound";
import Projects from "../platforms/Onboarding/Projects";
import SetUp from "../platforms/Integrations/SetUp";
import IntegrationSetting from "../platforms/Integrations/IntegrationSetting";
import IntegrationDetails from "../platforms/Integrations/IntegrationDetails";
import SourceSelection from "../platforms/Onboarding/SourceSelection";
import SourceGuard from "./GuardComponent";
import CreateMeetingOnboarding from "../platforms/Onboarding/CreateMeeting";
import TasksList from "../platforms/Tasks/TasksList";
import ConversationList from "../platforms/Conversation/ConversationList";
import TeamMemberList from "../platforms/TeamMember/TeamMemberList";
import Dashboard from "../platforms/Dashboard/Dashboard";
import SocialMediaScraping from "../platforms/SocialMediaScraping";
import SocialMediaScrapingDetail from "../platforms/SocialMediaScrapingDetail";

const NO_LAYOUT_PREFIXES = ["/onboarding"];

const Layout = () => {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const hideLayout = NO_LAYOUT_PREFIXES.some(
    (prefix) =>
      location.pathname === prefix ||
      location.pathname.startsWith(`${prefix}/`),
  );

  if (hideLayout) {
    return <Outlet />;
  }

  return (
    <>
      <Header />
      <div className="d-flex">
        <div
          className="sidebar-container col-auto"
          style={{
            width: sidebarCollapsed ? 64 : undefined,
            minWidth: sidebarCollapsed ? 64 : undefined,
            transition: "width .2s ease, min-width .2s ease",
            flex: sidebarCollapsed ? "0 0 64px" : undefined,
          }}
        >
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
          />
        </div>
        <div
          className="col py-3"
          style={{
            transition: "margin-left .2s ease, width .2s ease",
            minWidth: 0,
          }}
        >
          <Outlet />
        </div>
      </div>
    </>
  );
};

const OnboardingLayout = () => {
  return (
    <div>
      <Outlet />
    </div>
  );
};

const Platforms = () => (
  <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<Navigate to="agents" replace />} />
      <Route path="my-assistants" element={<AssistantList />} />
      <Route path="my-assistants/create" element={<NewAssistant />} />

      <Route path="my-assistants/:uuid" element={<AssistantDetail />}>
        <Route path="setting" element={<SettingsForm />} />
        <Route path="setting/:settingId/edit" element={<SettingsForm />} />
        <Route path="configuration" element={<ConfigurationForm />} />
        <Route
          path="configuration/:configurationId/edit"
          element={<ConfigurationForm />}
        />
        <Route path="tool" element={<ToolEditor />} />
        <Route path="tool/:toolId/edit" element={<ToolEditor />} />
      </Route>

      <Route path="apps" element={<Assistants />} />
      <Route path="apps/install/:assistantId" element={<AssistantDetails />} />
      <Route
        path="apps/installed/:assistantId/setting/:settingId"
        element={<AssistantDetails />}
      />
      <Route
        path="/apps/installed/:assistantId/setting/:settingId/webhook"
        element={<ConfigureWebhook />}
      />
      <Route
        path="apps/:assistantId/config/setting/:settingId"
        element={<PlatformWorkspace />}
      />
      <Route
        path="apps/:assistantId/config/setting/:settingId/update"
        element={<PlatformWorkspace />}
      />

      <Route path="agent" element={<CreateAgent />} />
      <Route path="agent/:agentId" element={<AgentTabs />}>
        <Route path="configuration" element={<ConfigurationForm />} />
        <Route
          path="configuration/:configurationId/edit"
          element={<ConfigurationForm />}
        />
      </Route>

      <Route path="profile" element={<Profile />} />
      <Route path="users" element={<Users />} />
      <Route path="users/create" element={<AddUser />} />
      <Route path="setting/:uuid" element={<Settings />} />
      <Route path="platform" element={<Platform />} />
      <Route path="platform/create" element={<NewPlatform />} />
      <Route path="/social-media-scraping" element={<SocialMediaScraping />} />
      <Route
        path="/social-media-scraping/:taskId"
        element={<SocialMediaScrapingDetail />}
      />

      <Route path="meetings">
        <Route index element={<MeetingsList />} />
        <Route path="create" element={<CreateMeeting />} />
        <Route path=":meetingId" element={<MeetingDetails />} />
      </Route>

      <Route path="participants" element={<Participants />} />
      <Route path="participants/create" element={<CreateParticipant />} />
      <Route path="participants/:uuid" element={<CreateParticipant />} />

      <Route path="team-members" element={<TeamMemberList />} />
      <Route path="billing" element={<Billing />} />

      <Route path="pricing" element={<Pricing />} />
      <Route path="add-credits" element={<CheckoutForm />} />
      <Route path="apps/coming-soon" element={<ComingSoon />} />
      <Route path="plan-details" element={<PlanDetails />} />
      <Route path="setup/:id/" element={<SetUp />} />
      <Route path="tasks" element={<TasksList />} />
      <Route path="my-tasks" element={<TasksList />} />
      <Route path="conversations" element={<ConversationList />} />
      <Route path="dashboard" element={<Dashboard />} />

      <Route path="integrations">
        <Route index element={<Integrations />} />
        <Route
          path="configuration/:settingId"
          element={<IntegrationSetting />}
        />
        <Route path=":id" element={<IntegrationDetails />} />
      </Route>
    </Route>

    <Route path="onboarding" element={<OnboardingLayout />}>
      <Route index element={<Navigate to="source" replace />} />
      <Route path="source" element={<SourceSelection />} />
      <Route path=":source/integrations" element={<SourceGuard />} />{" "}
      <Route path="setup/:id/:name" element={<SetUp />} />
      <Route path="apps" element={<Apps />} />
      <Route
        path="apps/projects/:assistantId/:settingId"
        element={<Projects />}
      />
      <Route
        path="integrations/configuration/:settingId"
        element={<IntegrationSetting />}
      />
      <Route path="meetings">
        <Route index element={<Meetings />} />
      </Route>
      <Route path="create-meeting" element={<CreateMeetingOnboarding />} />
      <Route path="plans" element={<Plans />} />
    </Route>

    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default Platforms;
